import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Input from '../Input';
import CustomDropdown from './CustomDropdown';

function AllArticles({page_title}) {

    useEffect(() => {
                            document.title = page_title;
                        }, [page_title]);


    const [publishedIssues, setPublishedIssues] = useState([]);
    const [selectedIssue, setSelectedIssue] = useState('all');
    const [selectedType, setSelectedType] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();

    const articleTypes = [
        { label: "All Article Types", value: "all" },
        { label: "Original Articles", value: "original article" },
        { label: "Reviews", value: "review" },
        { label: "Case Reports", value: "case report" },
        { label: "Letters to the Editor", value: "letter to the editor" },
        { label: "Commentaries", value: "commentary" },
        { label: "Book Reviews", value: "book review" },
        { label: "Professional Papers", value: "professional paper" },
        { label: "Editorials", value: "editorial" },
        { label: "Conference Paper", value: "conference paper" }
    ];

    // Step 1: Load issues and initialize filters from URL
    useEffect(() => {
        fetch('https://annalsofnursing.org/aon_backend/api/api.php?action=getAllIssues')
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    const published = data.issues.filter(issue => issue.publish === 1);
                    const formatted = published.map(issue => ({
                        label: issue.title + " (" + issue.date.slice(0, 4) + ")",
                        value: String(issue.id),
                        date_published: issue.date
                    }));
                    const allIssues = [{ label: "All Issues", value: "all" }, ...formatted];
                    setPublishedIssues(allIssues);

                    // Initialize filters from URL
                    const issueParam = searchParams.get('issue') || 'all';
                    const typeParam = searchParams.get('type') || 'all';
                    const queryParam = searchParams.get('q') || '';

                    const validIssue = allIssues.find(i => i.value === issueParam) ? issueParam : 'all';

                    setSelectedIssue(validIssue);
                    setSelectedType(typeParam);
                    setSearchQuery(queryParam);
                    setCommittedSearchQuery(queryParam);
                

                    filterArticles(validIssue, typeParam, queryParam);
                }
            })
            .catch(err => console.error('Failed to fetch issues:', err));
    }, []);

    const filterArticles = (issue, type, query) => {
        setSearchParams({ issue, type, q: query });
    };

    const handleSearchSubmit = () => {
        setCommittedSearchQuery(searchQuery);
        filterArticles(selectedIssue, selectedType, searchQuery);
    };

    // Load and filter articles
    const [articles, setArticles] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;

    const [committedSearchQuery, setCommittedSearchQuery] = useState('');

    const handleInputChange = (e) => {
        const val = e.target.value;
        setSearchQuery(val);

        // If input cleared, commit empty string to trigger reset
        if (val.trim() === '') {
            setCommittedSearchQuery('');
            filterArticles(selectedIssue, selectedType, ''); // Optional: or set searchParams here
        }
    };


    useEffect(() => {
        fetch("https://annalsofnursing.org/aon_backend/api/api.php?action=getAllArticles")
            .then(res => res.json())
            .then(data => {
            if (data.success) {
                let filtered = data.articles.filter(article => article.publish === 1);

                if (selectedIssue !== 'all') {
                filtered = filtered.filter(article => article.issue_id.toString() === selectedIssue);
                }

                if (selectedType !== 'all') {
                    const decodedType = decodeURIComponent(selectedType).replace(/\+/g, ' ');
                    filtered = filtered.filter(article => 
                        article.type.toLowerCase() === decodedType.toLowerCase()
                    );
                }

                if (committedSearchQuery.trim() !== '') {
                const lowerQ = committedSearchQuery.toLowerCase();
                filtered = filtered.filter(article =>
                    article.title.toLowerCase().includes(lowerQ) ||
                    article.authors.toLowerCase().includes(lowerQ) ||
                    article.keywords.toLowerCase().includes(lowerQ)
                );
                }

                setArticles(filtered);
                setCurrentPage(1);
            }
            })
            .catch(err => console.log(err));
    }, [selectedIssue, selectedType, committedSearchQuery]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = articles.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(articles.length / itemsPerPage);


    function changePage(pageNum) {
        setCurrentPage(currentPage + pageNum);

        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 0);
    }

    function getPublishedDate(articleIssueId, publishedIssues) {
        const issue = publishedIssues.find(
            (issue) => issue.value === String(articleIssueId)
        );

        if (!issue || !issue.date_published) return null;

        const date = new Date(issue.date_published);
        const options = { day: '2-digit', month: 'short', year: 'numeric' };

        return date.toLocaleDateString('en-GB', options).replace(/ /g, '-');
    }

    const openArticlePdf = (file, e) => {
        e.stopPropagation();
        e.preventDefault();

        if (file) {
            // Split into path + filename
            const lastSlashIndex = file.lastIndexOf('/');
            let path = '';
            let filename = file;

            if (lastSlashIndex !== -1) {
                path = file.substring(0, lastSlashIndex + 1); // include trailing slash
                filename = file.substring(lastSlashIndex + 1);
            }

            // Encode only the filename, not the whole path
            const encodedFile = path + encodeURIComponent(filename);

            window.open(encodedFile, '_blank');
        }
    };

    const handleResetFilters = () => {
        setSelectedIssue('all');
        setSelectedType('all');
        setSearchQuery('');
        setCommittedSearchQuery('');

        // Reset URL params
        const url = new URL(window.location.href);
        url.searchParams.delete('issue');
        url.searchParams.delete('type');
        url.searchParams.delete('search');
        window.history.replaceState({}, '', url.pathname);
    };


    return (
        <div className='instructions_section'>
            <div className="instructions_wrapper">
                <h2 className='instructions_title'>Articles</h2>
                <hr className="instructions_hr" />

                <div className='filter_by_wrapper'>
                    Filter by:
                    <div className="filters_wrapper">
                        <CustomDropdown
                            options={publishedIssues}
                            defaultValue={selectedIssue}
                            onSelect={(val) => {
                                setSelectedIssue(val);
                                filterArticles(val, selectedType, searchQuery);
                            }}
                        />
                        <CustomDropdown
                            options={articleTypes}
                            defaultValue={selectedType}
                            onSelect={(val) => {
                                setSelectedType(val);
                                filterArticles(selectedIssue, val, searchQuery);
                            }}
                        />
                    </div>
                </div>

                <Input
                    type="text"
                    class="hero_search"
                    placeholder="Enter Search Term"
                    value={searchQuery}
                    onChange={handleInputChange}
                    onSearch={handleSearchSubmit}
                />

                <div className='search_by_wrapper'>
                    Search by:
                    <div className='search_by_block'>article title</div>
                    <div className='search_by_block'>keyword</div>
                    <div className='search_by_block'>author</div>
                </div>

                <div className='articles_section_wrapper'>
                    {currentItems.length === 0 ? (
                        <p className="articles_no_data">
                            There are no records matching your filters. Please <span className='clear_article_filters' onClick={handleResetFilters}>clear the filters</span> to view all articles.
                        </p>
                        ) : (
                        currentItems.map(article => (
                            <Link 
                            className='article_block_link' 
                            to={`/article/${article.id}/${article.title.toLowerCase().replace(/\s+/g, '-')}`}
                            key={article.id}
                            >
                            <div className="article_block_wrapper">

                                <h3 className="article_block_title">
                                {article.title}
                                </h3>

                                <p className='article_block_authors'><span className='article_block_type'>{article.type.toUpperCase()}</span> | BY {article.authors.toUpperCase()}</p>

                                <p className='article_block_date'>
                                <span className="fa-regular fa-calendar"></span>&nbsp;
                                {getPublishedDate(article.issue_id, publishedIssues)}
                                </p>

                                <div className='article_block_key_words'>
                                {article.keywords?.split(',').map((keyword, index) => (
                                    <div key={index} className="article_block_key_word">
                                    {keyword.trim()}
                                    </div>
                                ))}
                                </div>

                                <div className='article_pdf_section'>
                                <div 
                                    className='article_pdf_block' 
                                    onClick={(e) => openArticlePdf(article.file, e)}
                                >
                                    <span className="fa-solid fa-file-pdf"></span>
                                </div>
                                </div>

                            </div>
                            </Link>
                        ))
                    )}

                    <div style={{ marginTop: "1rem", textAlign: "center", display: "flex", justifyContent: "center", alignItems: "center", gap: "1rem" }}>
                        {/* Left Arrow */}
                        <button
                            onClick={() => {changePage(-1)}}
                            disabled={currentPage === 1}
                            style={{
                            height: "40px",
                            width: "40px",
                            backgroundColor: "#eee",
                            color: "#000",
                            border: "none",
                            borderRadius: "100px",
                            cursor: currentPage === 1 ? "not-allowed" : "pointer",
                            opacity: currentPage === 1 ? 0.5 : 1
                            }}
                        >
                            <span className="fa-solid fa-arrow-left"></span>
                        </button>

                        {/* Page Info */}
                        <span>
                            Page {currentPage} of {totalPages}
                        </span>

                        {/* Right Arrow */}
                        <button
                            onClick={() => {changePage(1)}}
                            disabled={currentPage === totalPages}
                            style={{
                            height: "40px",
                            width: "40px",
                            backgroundColor: "#eee",
                            color: "#000",
                            border: "none",
                            borderRadius: "100px",
                            cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                            opacity: currentPage === totalPages ? 0.5 : 1
                            }}
                        >
                            <span className="fa-solid fa-arrow-right"></span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AllArticles;
